<template>
  <div v-if="templateType && templateType.meta">
    <!-- Modo completo: metadados técnicos Meta -->
    <v-row v-if="!isMinimal" dense>
      <v-col cols="12" sm="4">
        <v-text-field
          :value="meta.name"
          label="Nome (Meta)"
          readonly
          outlined
          dense
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="4">
        <v-text-field
          :value="meta.language"
          label="Idioma"
          readonly
          outlined
          dense
          hide-details
        />
      </v-col>
      <v-col cols="6" sm="4">
        <v-text-field
          :value="meta.category"
          label="Categoria"
          readonly
          outlined
          dense
          hide-details
        />
      </v-col>
    </v-row>

    <v-alert v-if="isAuthentication && !isMinimal" type="info" text dense class="text-body-2 mb-3">
      Templates de autenticação usam texto fixo definido pela Meta. Você pode
      configurar o aviso de segurança e a expiração do código.
    </v-alert>

    <!-- Modo completo: tabela de variáveis -->
    <template v-if="!isMinimal">
      <v-subheader class="px-0">Variáveis no envio</v-subheader>
      <v-simple-table dense class="mb-4 meta-vars-table">
        <template v-slot:default>
          <thead>
            <tr>
              <th>Variável</th>
              <th>Descrição</th>
              <th>Exemplo (Meta)</th>
              <th v-if="showInsertCol"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="v in templateType.variables" :key="v.name">
              <td><code>{{ formatVar(v.name) }}</code></td>
              <td>
                {{ v.description || "—" }}
                <span v-if="v.smsAllowed === false" class="text--secondary">
                  (apenas WhatsApp)
                </span>
              </td>
              <td>{{ v.example || "—" }}</td>
              <td v-if="showInsertCol">
                <v-btn
                  x-small
                  text
                  color="primary"
                  :disabled="!canInsertVariable"
                  @click="insertVariable(v.name)"
                >
                  Inserir
                </v-btn>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </template>

    <v-subheader v-if="!isMinimal" class="px-0">Conteúdo</v-subheader>

    <template v-if="isAuthentication">
      <v-switch
        v-model="local.components.body.add_security_recommendation"
        label="Incluir aviso de segurança"
        dense
        hide-details
        class="mt-0 mb-3"
        @change="emitChange"
      />

      <v-text-field
        v-if="showFooter"
        v-model.number="local.components.footer.code_expiration_minutes"
        type="number"
        min="1"
        max="90"
        label="Expiração do código (minutos)"
        :readonly="!footerExpirationEditable"
        outlined
        dense
        :rules="expirationRules"
        hint="Entre 1 e 90 minutos"
        persistent-hint
        class="mb-3"
        @input="emitChange"
      />
    </template>

    <template v-else-if="!isMinimal">
      <v-text-field
        v-if="showHeader && !isImageHeader"
        v-model="local.components.header.text"
        label="Cabeçalho (HEADER)"
        :readonly="!headerIsEditable"
        outlined
        dense
        class="mb-2"
        @input="emitChange"
      />

      <div class="mb-2">
        <v-textarea
          ref="bodyTextarea"
          v-model="local.components.body.text"
          label="Corpo da mensagem (BODY)"
          :placeholder="bodyPlaceholder"
          :readonly="!bodyIsEditable"
          outlined
          auto-grow
          rows="3"
          :rules="bodyRules"
          @input="emitChange"
        />
        <div v-if="bodyIsEditable" class="d-flex flex-wrap gap-1 mt-1">
          <v-chip
            v-for="v in bodyVariables"
            :key="'chip-' + v.name"
            small
            class="px-2 var-chip"
            :color="bodyChipColor(v)"
            :outlined="!bodyVarPresent(v.name)"
            clickable
            @click="insertVariable(v.name)"
          >
            {{ formatVar(v.name) }}
          </v-chip>
        </div>
      </div>

      <v-text-field
        v-if="showFooter"
        v-model="local.components.footer.text"
        label="Rodapé (FOOTER)"
        :readonly="!footerIsEditable"
        outlined
        dense
        class="mb-2"
        @input="emitChange"
      />
    </template>

    <!-- Modo mínimo: ticket/refund — editor + preview lado a lado -->
    <template v-else-if="isMinimal">
      <div class="meta-minimal-layout">
        <div class="meta-minimal-layout__editor">
          <div v-if="bodyIsEditable" class="mb-3">
            <v-textarea
              ref="bodyTextarea"
              v-model="local.components.body.text"
              label="Mensagem do WhatsApp"
              :placeholder="bodyPlaceholder"
              outlined
              auto-grow
              rows="3"
              :rules="minimalBodyRules"
              hide-details="auto"
              @input="emitChange"
            />
            <div class="d-flex flex-wrap gap-1 mt-2">
              <v-chip
                v-for="v in bodyVariables"
                :key="'chip-' + v.name"
                small
                class="px-2 var-chip"
                :color="bodyChipColor(v)"
                :outlined="!bodyVarPresent(v.name)"
                clickable
                @click="insertVariable(v.name)"
              >
                {{ formatVar(v.name) }}
              </v-chip>
            </div>
          </div>

          <template v-if="showCoverToggle">
            <v-switch
              v-model="local.components.header.include"
              label="Incluir capa do evento"
              dense
              hide-details
              class="mt-0 mb-0"
              @change="emitChange"
            />
            <p class="text-caption text--secondary mb-0 mt-1">
              Usa a imagem do evento enviada pelo sistema no envio.
            </p>
          </template>

          <div v-if="schemaButtons.length" class="mt-4">
            <v-text-field
              v-model="local.components.buttons[0].text"
              label="Texto do botão"
              outlined
              dense
              hide-details="auto"
              :rules="buttonTextRules"
              @input="emitChange"
            />
            <v-text-field
              v-if="showButtonUrlField"
              v-model="local.components.buttons[0].url"
              label="Link do botão"
              outlined
              dense
              hide-details="auto"
              class="mt-2"
              @input="emitChange"
            />
          </div>
        </div>

        <div class="meta-minimal-layout__preview">
          <div class="text-caption text--secondary mb-2 font-weight-medium">
            Pré-visualização
          </div>
          <whatsapp-preview
            :body-html="formattedPreviewBody"
            :footer="previewFooter"
            :image-url="showCoverInPreview ? previewHeaderImage : null"
            :buttons="previewButtons"
          />
        </div>
      </div>
    </template>

    <!-- Modo completo: botões -->
    <div v-if="!isMinimal && schemaButtons.length" class="mb-4">
      <v-subheader class="px-0">Botões</v-subheader>
      <div
        v-for="(btn, idx) in schemaButtons"
        :key="'btn-' + idx"
        class="d-flex align-center gap-2 mb-2"
      >
        <v-chip x-small label>{{ buttonTypeLabel(btn) }}</v-chip>
        <span v-if="isAuthentication" class="text-body-2 text--secondary">
          Texto definido pela Meta
        </span>
        <v-text-field
          v-else
          :value="local.components.buttons[idx]?.text || btn.text"
          :label="btn.editable === false ? 'Texto do botão (fixo)' : 'Texto do botão'"
          readonly
          outlined
          dense
          hide-details
          class="flex-grow-1"
        />
      </div>
    </div>

    <div v-if="!isMinimal" class="mt-2">
      <div class="text-caption text--secondary mb-2 font-weight-medium">
        Pré-visualização
      </div>
      <whatsapp-preview
        :body-html="formattedPreviewBody"
        :footer="previewFooter"
        :image-url="showCoverInPreview ? previewHeaderImage : null"
        :header-text="!isImageHeader && showHeader ? previewHeader : null"
        :buttons="previewButtons"
      />
    </div>
  </div>
</template>

<script>
import WhatsappPreview from "./WhatsappPreview.vue";
import {
  normalizeMetaMetadata,
  buildDefaultMetaMetadata,
  renderPreviewText,
  buildAuthenticationPreview,
  headerEnabled,
  footerEnabled,
  bodyEditable,
  footerExpirationEditable,
  isAuthenticationEditor,
  isImageHeader as isImageHeaderEditor,
  headerImageVariable,
  isMinimalEditor,
  headerUserToggle,
  endsWithVariable,
} from "@/utils/metaTemplateUtils.js";

export default {
  name: "MetaTemplateEditor",
  components: { WhatsappPreview },
  props: {
    templateType: { type: Object, default: null },
    value: { type: Object, default: null },
  },
  data: () => ({
    local: null,
    expirationRules: [
      (v) => (v !== "" && v != null) || "Informe a expiração",
      (v) => (v >= 1 && v <= 90) || "Entre 1 e 90 minutos",
    ],
  }),
  computed: {
    requiredVariables() {
      return (this.templateType?.variables || []).filter((v) => v.required);
    },
    buttonTextRules() {
      return [(v) => !!(v && v.trim()) || "Texto do botão é obrigatório"];
    },
    urlButtonVariable() {
      const btn = (this.editor.buttons || []).find(
        (b) => b.type === "URL" && b.urlVariable
      );
      return btn?.urlVariable || null;
    },
    showButtonUrlField() {
      const btn = this.schemaButtons[0];
      return !!btn && btn.urlEditable !== false;
    },
    trailingVariableRule() {
      return (v) =>
        !endsWithVariable(v || "") ||
        "A mensagem não pode terminar com uma variável. Adicione um texto após a última variável (pontuação, espaços, quebras de linha e caracteres especiais não contam).";
    },
    bodyRules() {
      return [
        (v) => !!v || "Texto do corpo é obrigatório",
        () =>
          this.missingRequiredVariables.length === 0 ||
          `Inclua: ${this.missingRequiredVariables
            .map((name) => this.formatVar(name))
            .join(", ")}`,
        this.trailingVariableRule,
      ];
    },
    minimalBodyRules() {
      return [
        (v) => !!(v && v.trim()) || "A mensagem é obrigatória",
        () =>
          this.missingRequiredVariables.length === 0 ||
          `Inclua: ${this.missingRequiredVariables
            .map((name) => this.formatVar(name))
            .join(", ")}`,
        this.trailingVariableRule,
      ];
    },
    missingRequiredVariables() {
      const text = this.local?.components?.body?.text || "";
      const imageVar = this.isImageHeader ? this.headerImageVar : null;
      const urlVar = this.urlButtonVariable;
      return this.requiredVariables
        .filter((v) => !v.derived)
        .map((v) => v.name)
        .filter((name) => name !== imageVar && name !== urlVar)
        .filter((name) => !this.bodyContainsVariable(text, name));
    },
    meta() {
      return this.templateType?.meta || {};
    },
    editor() {
      return this.meta.editor || {};
    },
    isMinimal() {
      return isMinimalEditor(this.editor);
    },
    isAuthentication() {
      return isAuthenticationEditor(this.editor);
    },
    showHeader() {
      return headerEnabled(this.editor);
    },
    isImageHeader() {
      return isImageHeaderEditor(this.editor);
    },
    showCoverToggle() {
      return this.isMinimal && headerUserToggle(this.editor);
    },
    showCoverInPreview() {
      if (!this.isImageHeader) return false;
      if (this.showCoverToggle) {
        return !!this.local?.components?.header?.include;
      }
      return true;
    },
    headerImageVar() {
      return headerImageVariable(this.editor);
    },
    showFooter() {
      return footerEnabled(this.editor);
    },
    bodyIsEditable() {
      return bodyEditable(this.editor);
    },
    bodyPlaceholder() {
      return this.editor?.body?.defaultText || "";
    },
    footerExpirationEditable() {
      return footerExpirationEditable(this.editor);
    },
    headerIsEditable() {
      return this.showHeader && this.editor.header?.editable !== false;
    },
    footerIsEditable() {
      return this.showFooter && this.editor.footer?.editable !== false;
    },
    schemaButtons() {
      if (this.editor.buttons === false) return [];
      return Array.isArray(this.editor.buttons) ? this.editor.buttons : [];
    },
    showInsertCol() {
      return this.bodyIsEditable;
    },
    canInsertVariable() {
      return this.bodyIsEditable;
    },
    bodyVariables() {
      const vars = (this.templateType?.variables || []).filter((v) => !v.derived);
      const exclude = [this.headerImageVar, this.urlButtonVariable].filter(Boolean);
      return vars.filter((v) => !exclude.includes(v.name));
    },
    authPreview() {
      if (!this.isAuthentication) return null;
      return buildAuthenticationPreview(this.local, this.templateType);
    },
    previewBody() {
      if (this.authPreview) return this.authPreview.body;
      return renderPreviewText(
        this.local?.components?.body?.text,
        this.templateType?.variables
      );
    },
    previewFooter() {
      if (this.authPreview) return this.authPreview.footer;
      if (this.showFooter && this.local?.components?.footer?.text) {
        return this.local.components.footer.text;
      }
      return null;
    },
    previewHeader() {
      return renderPreviewText(
        this.local?.components?.header?.text,
        this.templateType?.variables
      );
    },
    previewHeaderImage() {
      if (!this.isImageHeader) return null;
      const variable = (this.templateType?.variables || []).find(
        (v) => v.name === this.headerImageVar
      );
      return variable?.example || null;
    },
    formattedPreviewBody() {
      return this.formatWhatsAppText(this.previewBody);
    },
    previewButtons() {
      if (!this.schemaButtons.length) return [];
      return this.schemaButtons.map((btn, idx) =>
        this.previewButtonLabel(btn, idx)
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(v) {
        this.syncFromValue(v);
      },
    },
    templateType() {
      this.syncFromValue(this.value);
    },
  },
  methods: {
    formatVar(name) {
      return `{{${name}}}`;
    },
    bodyContainsVariable(text, name) {
      if (!text) return false;
      const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      return new RegExp(`{{\\s*${escaped}\\s*}}`).test(text);
    },
    bodyVarPresent(name) {
      return this.bodyContainsVariable(
        this.local?.components?.body?.text || "",
        name
      );
    },
    bodyChipColor(v) {
      if (this.bodyVarPresent(v.name)) return "primary";
      if (v.required) return "error";
      return undefined;
    },
    buttonTypeLabel(btn) {
      if (btn.otp_type === "COPY_CODE") return "Copiar código";
      if (btn.type === "URL") return "URL";
      if (btn.type === "PHONE_NUMBER") return "Telefone";
      if (btn.type === "QUICK_REPLY") return "Resposta rápida";
      return btn.type || "Botão";
    },
    previewButtonLabel(btn, idx) {
      if (this.isAuthentication) return "Copiar código";
      const i = idx != null ? idx : this.schemaButtons.indexOf(btn);
      return this.local?.components?.buttons?.[i]?.text || btn.text || "Botão";
    },
    formatWhatsAppText(text) {
      if (!text) return "";
      const escaped = String(text)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
      return escaped.replace(/\*([^*\n]+)\*/g, "<strong>$1</strong>");
    },
    syncFromValue(v) {
      if (!this.templateType) {
        this.local = null;
        return;
      }
      const normalized =
        normalizeMetaMetadata(v, this.templateType) ||
        buildDefaultMetaMetadata(this.templateType);
      this.local = normalized;
      this.ensureStructure();
    },
    ensureStructure() {
      if (!this.local) return;
      if (!this.local.components) this.local.components = {};

      if (this.isAuthentication) {
        if (!this.local.components.body) {
          this.local.components.body = {
            add_security_recommendation:
              this.editor.body?.defaultAddSecurityRecommendation ?? true,
          };
        }
        if (this.showFooter && !this.local.components.footer) {
          this.local.components.footer = {
            code_expiration_minutes:
              this.editor.footer?.codeExpirationMinutes ?? 10,
          };
        }
      }

      if (this.isImageHeader) {
        const headerDef = this.editor.header || {};
        const varName = headerDef.variable || "party.cover";
        if (!this.local.components.header) {
          this.local.components.header = {
            format: "IMAGE",
            image: `{{${varName}}}`,
            include: headerDef.defaultInclude != null ? headerDef.defaultInclude : true,
          };
        } else if (this.local.components.header.include == null) {
          this.local.components.header.include =
            headerDef.defaultInclude != null ? headerDef.defaultInclude : true;
        }
      }

      if (this.schemaButtons.length) {
        if (!Array.isArray(this.local.components.buttons)) {
          const defaults = this.templateType?.defaultMetadata?.components?.buttons;
          this.local.components.buttons = this.schemaButtons.map((b, i) => ({
            type: b.type,
            text: defaults?.[i]?.text ?? b.text,
            url: defaults?.[i]?.url,
          }));
        } else {
          for (let i = 0; i < this.schemaButtons.length; i++) {
            const schema = this.schemaButtons[i];
            const stored = this.local.components.buttons[i] || {};
            const defaults = this.templateType?.defaultMetadata?.components?.buttons?.[i];
            if (!stored.type) stored.type = schema.type;
            if (!stored.text) stored.text = defaults?.text ?? schema.text;
            if (!stored.url && (defaults?.url || schema.urlPreview)) {
              stored.url = defaults?.url ?? schema.urlPreview;
            }
            this.local.components.buttons[i] = stored;
          }
        }
      }
    },
    insertVariable(name) {
      if (!this.canInsertVariable || !this.local?.components?.body) return;
      const token = this.formatVar(name);
      const textarea = this.$refs.bodyTextarea?.$refs?.input;
      const current = this.local.components.body.text || "";

      if (textarea && typeof textarea.selectionStart === "number") {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        this.local.components.body.text =
          current.slice(0, start) + token + current.slice(end);
        this.$nextTick(() => {
          textarea.focus();
          const pos = start + token.length;
          textarea.setSelectionRange(pos, pos);
        });
      } else {
        this.local.components.body.text = current + token;
      }
      this.emitChange();
    },
    emitChange() {
      if (!this.local) return;
      this.ensureStructure();
      this.$emit("input", JSON.parse(JSON.stringify(this.local)));
    },
    validate() {
      if (this.isAuthentication) {
        const minutes = this.local?.components?.footer?.code_expiration_minutes;
        if (this.showFooter && (minutes == null || minutes < 1 || minutes > 90)) {
          return false;
        }
        return true;
      }
      if (this.bodyIsEditable) {
        const text = this.local?.components?.body?.text;
        if (!text || !text.trim()) return false;
        if (this.missingRequiredVariables.length) return false;
        if (endsWithVariable(text)) return false;
        if (this.schemaButtons.length) {
          const btnText = this.local?.components?.buttons?.[0]?.text;
          if (!btnText || !btnText.trim()) return false;
        }
        return true;
      }
      return !!this.local?.components?.body?.text;
    },
  },
};
</script>

<style scoped>
.meta-vars-table code {
  font-size: 0.85em;
}
.meta-minimal-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  align-items: start;
}
@media (min-width: 600px) {
  .meta-minimal-layout {
    grid-template-columns: 1fr minmax(220px, 280px);
  }
}
.meta-minimal-layout__preview {
  position: sticky;
  top: 0;
}
.gap-1 {
  gap: 4px;
}
.gap-2 {
  gap: 8px;
}
.var-chip {
  font-size: 0.8125rem;
  height: 26px;
}
</style>
